import 'Components/vm-bottom-sheet/vm-bottom-sheet';
import 'Video/components/vm-poll/vm-poll';
import type { PollOption } from 'Video/components/vm-poll/vm-poll';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dismissPoll, trackPollEvent } from 'Api/polls';
import Styles from './vm-poll-overlay.scss';

export interface PollAssignment {
    id: number;
    poll_id: number;
    title: string;
    options: PollOption[];
    trigger_seconds: number;
    duration: number;
    confirmation_duration: number;
}

const enum TrackingEvent {
    SHOWN = 'shown',
    SUBMIT = 'submit',
    MANUAL_DISMISS = 'manual_dismiss',
    FULLSCREEN_SUPPRESSED = 'fullscreen_suppressed',
    BACKGROUND_SUPPRESSED = 'background_suppressed',
}

/** Why a poll trigger was suppressed instead of shown. */
export type SuppressionReason = 'fullscreen' | 'background';

const SUPPRESSION_EVENTS: Record<SuppressionReason, TrackingEvent> = {
    fullscreen: TrackingEvent.FULLSCREEN_SUPPRESSED,
    background: TrackingEvent.BACKGROUND_SUPPRESSED,
};

const MOBILE_BREAKPOINT = 768;
const SHOWN_IDS_KEY = 'poll_shown_assignment_ids';

@customElement('vm-poll-overlay')
export class VmPollOverlay extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'viewed-uuid' })
    viewedUuid: string = '';

    @property({ type: Array })
    assignments: PollAssignment[] = [];

    // --- Internal state ---

    @state()
    private _activeAssignment: PollAssignment | null = null;

    @state()
    private _mode: 'overlay' | 'sheet' = 'overlay';

    // --- Shown assignment tracking (sessionStorage) ---
    // Survives back-forward navigations that reload the page from disk cache.

    private _submitting: boolean = false;
    private _elapsed: number = 0;
    private _resizeObserver: ResizeObserver | null = null;
    private _shownAt: number = 0;

    // --- Public API (called by polls-controller) ---

    showPoll(assignmentId: number): boolean {
        if (this._activeAssignment) return false;

        const assignment = this.assignments.find((a) => a.id === assignmentId);
        if (!assignment) return false;

        this._addShownId(assignmentId);
        this._activeAssignment = assignment;
        this._shownAt = Date.now();
        this._submitting = false;
        this._elapsed = 0;
        this._trackEvent(assignmentId, TrackingEvent.SHOWN);
        this._trackMixpanel('PollImpression', {
            trigger_seconds: assignment.trigger_seconds,
        });

        return true;
    }

    /**
     * Record that a poll trigger fired but was suppressed instead of shown, so
     * it isn't counted as an impression. Reasons:
     *  - `fullscreen`: the player is in native HTML5 fullscreen (e.g. iOS
     *    Safari) where DOM overlays cannot render.
     *  - `background`: the tab was hidden (user switched tabs/windows) while the
     *    video kept playing, so the user never saw the poll.
     * Does not mark the assignment as shown.
     */
    suppressPoll(assignmentId: number, reason: SuppressionReason = 'fullscreen'): boolean {
        const assignment = this.assignments.find((a) => a.id === assignmentId);
        if (!assignment) return false;

        trackPollEvent(assignmentId, {
            viewed_uuid: this.viewedUuid,
            event: SUPPRESSION_EVENTS[reason],
        }).catch(() => {});

        const context = (window as any).__VUMEDI_VIDEO_DETAIL_CONTEXT__;
        (window as any).vumedi?.mixpanel?.track('PollSuppressed', {
            poll_id: assignment.poll_id,
            assignment_id: assignment.id,
            video_id: context?.videoId,
            video_slug: context?.videoSlug,
            channel_id: context?.channelId || null,
            trigger_seconds: assignment.trigger_seconds,
            suppression_reason: reason,
        });

        return true;
    }

    hasBeenShown(assignmentId: number): boolean {
        try {
            const raw = sessionStorage.getItem(SHOWN_IDS_KEY);
            const ids: number[] = raw ? JSON.parse(raw) : [];
            return ids.includes(assignmentId);
        } catch {
            return false;
        }
    }

    // --- Lifecycle ---

    override connectedCallback() {
        super.connectedCallback();
        this._updateMode();

        // Track viewport changes to switch between overlay/sheet mode
        this._resizeObserver = new ResizeObserver(() => this._updateMode());
        this._resizeObserver.observe(document.documentElement);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this._resizeObserver?.disconnect();
    }

    // --- Mode detection ---

    private _updateMode() {
        this._mode = window.innerWidth < MOBILE_BREAKPOINT ? 'sheet' : 'overlay';
    }

    // --- Auto-dismiss ---
    // The countdown is driven by the polls-controller feeding in seconds of
    // actual player playback (not wall-clock). Pausing the video — including the
    // hover-to-pause — stops the ticks, so the countdown freezes, matching
    // Brightcove's in-video polls. This component is the single owner of whether
    // a poll is active and how much of its duration has elapsed.

    /**
     * Advance the active poll's auto-dismiss countdown by `deltaSeconds` of
     * playback, dismissing it once its `duration` elapses. No-op unless a poll
     * is showing and the user hasn't started submitting. Called by the
     * polls-controller on each player `timeupdate`.
     */
    advancePlayback(deltaSeconds: number) {
        if (!this._activeAssignment || this._submitting) return;
        this._elapsed += deltaSeconds;
        if (this._elapsed >= this._activeAssignment.duration) {
            this._autoDismiss();
        }
    }

    private _autoDismiss() {
        this._trackMixpanel('PollDismissed', {
            dismiss_type: 'auto',
            time_to_dismiss_ms: Date.now() - this._shownAt,
        });
        this._hidePoll();
    }

    // --- Event handlers (from vm-poll) ---

    private _handleSubmitting() {
        // User clicked an option — suppress auto-dismiss so the poll stays visible
        // while the API call completes.
        this._submitting = true;
    }

    private _handleResponded(e: CustomEvent) {
        const { assignmentId, optionId, shownPosition } = e.detail;
        this._trackEvent(assignmentId, TrackingEvent.SUBMIT, {
            selected_option_id: optionId,
            shown_position: shownPosition,
        });
        this._trackMixpanel('PollResponse', {
            option_id: optionId,
            shown_position: shownPosition,
            time_to_respond_ms: Date.now() - this._shownAt,
        });
        const confirmationDuration = this._activeAssignment?.confirmation_duration;
        this._hidePoll();
        this._showToast('Thank you for your response.', confirmationDuration);
    }

    private _handleDismissed() {
        if (this._activeAssignment) {
            this._trackEvent(this._activeAssignment.id, TrackingEvent.MANUAL_DISMISS);
        }
        this._trackMixpanel('PollDismissed', {
            dismiss_type: 'manual',
            time_to_dismiss_ms: Date.now() - this._shownAt,
        });
        this._hidePoll();
    }

    private _handleError() {
        // Re-enable auto-dismiss — the submit failed, so vm-poll has reset its own
        // state and the user may retry or walk away. Without this the countdown
        // stays frozen and the poll never auto-dismisses.
        this._submitting = false;
        document.dispatchEvent(
            new CustomEvent('vm-toast-show', {
                detail: {
                    id: 'poll-error-toast',
                    message: 'Something went wrong. Please try again.',
                    variant: 'error' as const,
                    icon: 'x-close',
                },
            }),
        );
    }

    /** Bottom sheet closed by user (close button, drag-to-dismiss, escape). */
    private _handleSheetClose() {
        if (!this._activeAssignment) return;

        // Fire the dismiss API call that vm-poll's close button would normally handle.
        dismissPoll(this._activeAssignment.id, this.viewedUuid).catch(() => {});

        this._handleDismissed();
    }

    // --- Helpers ---

    private _hidePoll() {
        this._activeAssignment = null;
        this._submitting = false;
        this.dispatchEvent(new CustomEvent('poll-hidden', { bubbles: true, composed: true }));
    }

    private _handleHover() {
        this.dispatchEvent(new CustomEvent('poll-hover', { bubbles: true, composed: true }));
    }

    private _showToast(message: string, duration?: number) {
        document.dispatchEvent(
            new CustomEvent('vm-toast-show', {
                detail: {
                    id: 'poll-toast',
                    message,
                    variant: 'confirmation' as const,
                    ...(duration != null ? { duration } : {}),
                    icon: 'check-circle',
                },
            }),
        );
    }

    private _trackEvent(assignmentId: number, event: TrackingEvent, data?: Record<string, unknown>) {
        trackPollEvent(assignmentId, { viewed_uuid: this.viewedUuid, event, ...data }).catch(() => {});
    }

    private _trackMixpanel(eventName: string, properties: Record<string, unknown> = {}) {
        const context = (window as any).__VUMEDI_VIDEO_DETAIL_CONTEXT__;
        (window as any).vumedi?.mixpanel?.track(eventName, {
            poll_id: this._activeAssignment?.poll_id,
            assignment_id: this._activeAssignment?.id,
            video_id: context?.videoId,
            video_slug: context?.videoSlug,
            channel_id: context?.channelId || null,
            ...properties,
        });
    }

    private _addShownId(assignmentId: number) {
        try {
            const raw = sessionStorage.getItem(SHOWN_IDS_KEY);
            const ids: number[] = raw ? JSON.parse(raw) : [];
            if (!ids.includes(assignmentId)) {
                ids.push(assignmentId);
                sessionStorage.setItem(SHOWN_IDS_KEY, JSON.stringify(ids));
            }
        } catch {
            // sessionStorage unavailable — poll won't be suppressed on reload
        }
    }

    private _renderPoll() {
        if (!this._activeAssignment) return nothing;

        const poll = html`
            <vm-poll
                assignment-id=${this._activeAssignment.id}
                poll-id=${this._activeAssignment.poll_id}
                viewed-uuid=${this.viewedUuid}
                .question=${this._activeAssignment.title}
                .options=${this._activeAssignment.options}
                mode=${this._mode}
                @poll-submitting=${this._handleSubmitting}
                @poll-responded=${this._handleResponded}
                @poll-dismissed=${this._handleDismissed}
                @poll-error=${this._handleError}
            ></vm-poll>
        `;

        if (this._mode === 'sheet') {
            return html`
                <vm-bottom-sheet open fullscreen data-testid="poll-overlay" @vm-close=${this._handleSheetClose}>
                    <span slot="header" class="poll-overlay__sheet-title">Poll</span>
                    ${poll}
                </vm-bottom-sheet>
            `;
        }

        return html`
            <div class="poll-overlay__backdrop" data-testid="poll-overlay-backdrop"></div>
            <div class="poll-overlay poll-overlay--overlay" data-testid="poll-overlay" @mouseenter=${this._handleHover}>
                ${poll}
            </div>
        `;
    }

    override render() {
        return this._renderPoll();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-poll-overlay': VmPollOverlay;
    }
}
